/**
 * useWebSpeechSTT Hook
 * Uses the browser's native Web Speech API (SpeechRecognition) for real-time voice input.
 * Passes the user's selected language directly for zero-latency detection.
 * Provides interim (real-time) results for live transcript display.
 *
 * Replaces useGoogleSTT — no network round-trip, no backend STT call.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../../../utils/logger';

// We reuse the Window.SpeechRecognition global type from useSpeechRecognition.ts.
// To avoid structural-typing conflicts between the two files' local interfaces,
// we use `any` for the recognition instance ref. Runtime behaviour is unchanged.

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface ISpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  addEventListener(type: string, listener: () => void): void;
}

type SpeechRecognitionCtorType = new () => ISpeechRecognition;

const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? (window as unknown as { SpeechRecognition?: SpeechRecognitionCtorType }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: SpeechRecognitionCtorType })
        .webkitSpeechRecognition ||
      null
    : null;

// ── Public API ──

export interface UseWebSpeechSTTOptions {
  /** Called when a final transcript is confirmed */
  onTranscript?: (transcript: string) => void;
  /** Called on any error */
  onError?: (error: string) => void;
  /** Called when speech audio is first detected (before any result) */
  onSpeechDetected?: () => void;
  /** Called when a recognition session ends with no usable result */
  onNoMatch?: (reason?: 'empty' | 'low_confidence') => void;
  /** If true, automatically restart recognition after each final result */
  continuousMode?: boolean;
  /** If true, suppress listening (cancel any active session) */
  isMuted?: boolean;
  /** BCP-47 language tag to force on the recogniser, e.g. "en-US", "tr-TR" */
  language?: string;
}

export interface UseWebSpeechSTTReturn {
  /** Whether the recogniser is currently active */
  isListening: boolean;
  /** Whether the browser supports SpeechRecognition at all */
  isSupported: boolean;
  /** The latest final transcript */
  transcript: string;
  /** Real-time interim transcript (updates as user speaks) */
  interimTranscript: string;
  /** Start recognition */
  startListening: () => void;
  /** Stop recognition gracefully (processes pending audio) */
  stopListening: () => void;
  /** Cancel recognition (discards pending audio) */
  cancelListening: () => void;
  /** Toggle start/stop */
  toggleListening: () => void;
  /** Last error message, if any */
  error: string | null;
}

export const useWebSpeechSTT = ({
  onTranscript,
  onError,
  onSpeechDetected,
  onNoMatch,
  continuousMode = false,
  isMuted = false,
  language = 'en-US',
}: UseWebSpeechSTTOptions): UseWebSpeechSTTReturn => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const isListeningRef = useRef(false);
  const isMutedRef = useRef(isMuted);
  const continuousModeRef = useRef(continuousMode);
  const languageRef = useRef(language);
  const hasFinalRef = useRef(false);
  const intentionalStopRef = useRef(false);

  // Stable callback refs — avoids re-creating the recognition instance on every render
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSpeechDetectedRef = useRef(onSpeechDetected);
  onSpeechDetectedRef.current = onSpeechDetected;
  const onNoMatchRef = useRef(onNoMatch);
  onNoMatchRef.current = onNoMatch;

  // Keep mutable refs in sync
  isMutedRef.current = isMuted;
  continuousModeRef.current = continuousMode;
  languageRef.current = language;

  const isSupported = !!SpeechRecognitionCtor;

  // ── Build / destroy recognition instance when language changes ──
  const buildRecognition = useCallback(() => {
    if (!SpeechRecognitionCtor) return null;

    const rec = new SpeechRecognitionCtor();
    rec.continuous = true; // Keep listening until we explicitly stop
    rec.interimResults = true; // Stream interim results for live transcript
    rec.maxAlternatives = 1;
    rec.lang = languageRef.current;

    rec.onstart = () => {
      isListeningRef.current = true;
      hasFinalRef.current = false;
      setIsListening(true);
      setInterimTranscript('');
      setError(null);
      logger.info('WebSpeechSTT', 'Recognition started', { lang: languageRef.current });
    };

    rec.addEventListener('speechstart', () => {
      onSpeechDetectedRef.current?.();
    });

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let finalText = '';
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }

      // Update interim display
      if (interimText) {
        setInterimTranscript(interimText);
      }

      // Process final result
      if (finalText) {
        const trimmed = finalText.trim();
        if (trimmed) {
          hasFinalRef.current = true;
          setTranscript(trimmed);
          setInterimTranscript('');
          logger.info('WebSpeechSTT', 'Final transcript', { transcript: trimmed });
          onTranscriptRef.current?.(trimmed);
        }
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      // "no-speech", "aborted", and "network" are non-fatal (they auto-restart in continuous mode)
      if (event.error === 'no-speech' || event.error === 'aborted' || event.error === 'network') {
        logger.debug('WebSpeechSTT', `Non-fatal error: ${event.error}`);
        return;
      }
      const msg = `Speech recognition error: ${event.error}`;
      logger.error('WebSpeechSTT', msg, new Error(msg));
      setError(msg);
      onErrorRef.current?.(msg);
      setIsListening(false);
      isListeningRef.current = false;
    };

    rec.onend = () => {
      isListeningRef.current = false;
      setIsListening(false);
      setInterimTranscript('');

      if (!hasFinalRef.current && !intentionalStopRef.current) {
        onNoMatchRef.current?.('empty');
      }

      // Auto-restart in continuous mode (unless muted or intentionally stopped)
      if (continuousModeRef.current && !isMutedRef.current && !intentionalStopRef.current) {
        logger.debug('WebSpeechSTT', 'Auto-restarting in continuous mode');
        setTimeout(() => {
          if (!isListeningRef.current && !isMutedRef.current) {
            try {
              rec.lang = languageRef.current; // pick up any language change
              rec.start();
            } catch {
              // Browser may throw if already started — ignore
            }
          }
        }, 100);
      }

      intentionalStopRef.current = false;
    };

    return rec;
  }, []);

  // Initialize once
  useEffect(() => {
    const rec = buildRecognition();
    recognitionRef.current = rec;
    return () => {
      if (rec) {
        try {
          rec.abort();
        } catch {
          /* already stopped */
        }
      }
    };
  }, [buildRecognition]);

  // ── Update language on the fly ──
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language;
      logger.debug('WebSpeechSTT', 'Language updated', { lang: language });
    }
  }, [language]);

  // ── Mute handling ──
  useEffect(() => {
    if (isMuted && isListeningRef.current && recognitionRef.current) {
      logger.info('WebSpeechSTT', 'Muted — aborting recognition');
      intentionalStopRef.current = true;
      try {
        recognitionRef.current.abort();
      } catch {
        /* noop */
      }
    } else if (!isMuted && continuousMode && !isListeningRef.current && recognitionRef.current) {
      // Unmuted in continuous mode — restart
      logger.info('WebSpeechSTT', 'Unmuted — restarting recognition');
      try {
        recognitionRef.current.lang = language;
        recognitionRef.current.start();
      } catch {
        /* noop */
      }
    }
  }, [isMuted, continuousMode, language]);

  // ── Public controls ──
  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListeningRef.current) return;
    intentionalStopRef.current = false;
    setTranscript('');
    setInterimTranscript('');
    setError(null);
    try {
      recognitionRef.current.lang = languageRef.current;
      recognitionRef.current.start();
    } catch (_err) {
      const msg = 'Failed to start speech recognition';
      setError(msg);
      onErrorRef.current?.(msg);
    }
  }, []);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListeningRef.current) return;
    intentionalStopRef.current = true;
    try {
      recognitionRef.current.stop(); // Graceful — fires pending results
    } catch {
      /* noop */
    }
  }, []);

  const cancelListening = useCallback(() => {
    if (!recognitionRef.current) return;
    intentionalStopRef.current = true;
    try {
      recognitionRef.current.abort(); // Hard stop — discards pending
    } catch {
      /* noop */
    }
    setInterimTranscript('');
  }, []);

  const toggleListening = useCallback(() => {
    if (isListeningRef.current) {
      stopListening();
    } else {
      startListening();
    }
  }, [startListening, stopListening]);

  return {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    cancelListening,
    toggleListening,
    error,
  };
};
